/* eslint-disable prettier/prettier */
import React from "react";
import { Typography } from "@mui/material";
import "./metricsview.scss";
import { METRICS_CONFIG } from "../../../utils/metricsConfig";
import { pageLocale } from "../../../utils/locale";
import { getMetricReading } from "../../../utils/helpers";

// A metric is renderable only if its value is a finite number. Anything
// else (null, undefined, "", NaN, non-numeric strings) means "no data" and
// we hide the metric entirely instead of showing an "N/A" placeholder.
const hasValidMetricValue = (v) => {
  if (v === null || v === undefined || v === "") return false;
  return Number.isFinite(Number(v));
};

const MetricsViewV2 = ({ reportData, reportType }) => {
  const mv2Metrics = pageLocale("metricsv2_metrics");
  const mv2Units = pageLocale("metricsv2_units");
  const { na } = pageLocale("common") || { na: "N/A" };

  // Format metric value with localized unit. Whole-number rounding for
  // count-style metrics, two decimals otherwise (one for percent).
  const formatValue = (value, metricConfig) => {
    if (!hasValidMetricValue(value)) return na || "N/A";
    const unit = mv2Units?.[metricConfig.metric] ?? "";
    const rawUnit = metricConfig.unit;

    if (rawUnit === "steps/min" || rawUnit === "count") {
      return unit ? `${Math.round(value)} ${unit}` : `${Math.round(value)}`;
    }

    if (rawUnit === "percentage (%)") {
      return `${Number(value).toFixed(1)} %`;
    }

    if (rawUnit === "unitless") {
      return `${Number(value).toFixed(2)}`;
    }

    return unit
      ? `${Number(value).toFixed(2)} ${unit}`
      : `${Number(value).toFixed(2)}`;
  };

  // Get raw metric object from reportData
  const getMetricData = (metricConfig) => {
    const { apiField, category } = metricConfig;
    if (!category || !apiField) return null;
    return reportData?.[category]?.[apiField] ?? null;
  };

  const getMetricValue = (metricConfig) => {
    const raw = getMetricData(metricConfig);
    if (raw === null || raw === undefined) return null;
    if (typeof raw !== "object") return raw;
    return getMetricReading(raw);
  };

  // Localized display name; fall back to a humanized metric key.
  const getMetricDisplayName = (metricConfig) =>
    mv2Metrics?.[metricConfig.metric] || metricConfig.metric.replace(/_/g, " ");

  // Only render metrics that are actually present in the report summary response,
  // and are relevant to the current testType (H or M).
  const visibleMetrics = METRICS_CONFIG.map((metricConfig) => ({
    metricConfig,
    value: getMetricValue(metricConfig),
  })).filter(({ metricConfig }) => {
    if (reportType === "front") {
      return metricConfig.tugFront === "H" || metricConfig.tugFront === "M";
    }
    if (reportType === "side") {
      return metricConfig.tugSide === "H" || metricConfig.tugSide === "M";
    }
    return false;
  });

  return (
    <div className="metrics-view-container">
      <div className="metrics-grid">
        {visibleMetrics.map(({ metricConfig, value }) => (
          <div key={metricConfig.metric} className="metric-card">
            <Typography className="metric-name">
              {getMetricDisplayName(metricConfig)}
            </Typography>
            <Typography className="metric-value">
              {formatValue(value, metricConfig.unit)}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricsViewV2;
